import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Row, Col } from 'reactstrap';
import { AiOutlineGroup } from 'react-icons/ai';
import { CgTemplate } from 'react-icons/cg';
import { TbAsset } from 'react-icons/tb';
import type { TaskOccurrence } from '../../../core/api/rtk/taskTemplatesApi';
import AssetDetailHeader from './AssetDetailHeader';
import DailyTasksSection from './DailyTasksSection';
import PreventiveMaintenanceSection from './PreventiveMaintenanceSection';
import ProjectsSection from './ProjectsSection';
import InventorySection from './InventorySection';

const NEUTRAL_COLOR = '#6c757d';
const NEUTRAL_BG = '#f6f6f8';

const nodeTypeConfig: Record<string, { color: string; bgColor: string; label: string }> = {
  group: { color: NEUTRAL_COLOR, bgColor: NEUTRAL_BG, label: 'Group' },
  item: { color: NEUTRAL_COLOR, bgColor: NEUTRAL_BG, label: 'Item' },
  template: { color: NEUTRAL_COLOR, bgColor: NEUTRAL_BG, label: 'Template' },
  collection: { color: NEUTRAL_COLOR, bgColor: NEUTRAL_BG, label: 'Collection' },
};

const HierarchyNode: React.FC<{
  node: any;
  depth: number;
  onSelectSource?: (source: { id: number; kind: 'group' | 'item' } | null | undefined) => void;
}> = ({ node, depth, onSelectSource }) => {
  const config = nodeTypeConfig[node.type] || nodeTypeConfig.item;
  const hasChildren = node.children && node.children.length > 0;
  const isSelectable =
    !!onSelectSource && (node.type === 'group' || node.type === 'item') && node.rawId != null;
  const handleSelect = () => {
    if (!isSelectable) return;
    onSelectSource!({ id: node.rawId, kind: node.type as 'group' | 'item' });
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!isSelectable) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSelect();
    }
  };

  return (
    <>
      <div
        className={`asset-detail__hierarchy-item${
          isSelectable ? ' asset-detail__hierarchy-item--clickable' : ''
        }`}
        style={{ paddingLeft: `${14 + depth * 20}px` }}
        onClick={isSelectable ? handleSelect : undefined}
        onKeyDown={isSelectable ? handleKeyDown : undefined}
        role={isSelectable ? 'button' : undefined}
        tabIndex={isSelectable ? 0 : undefined}
      >
        {node.type === 'template' ? (
          <CgTemplate className="asset-detail__hierarchy-icon" size={16} color={config.color} />
        ) : node.type === 'group' ? (
          <AiOutlineGroup className="asset-detail__hierarchy-icon" size={16} color={config.color} />
        ) : (
          <TbAsset className="asset-detail__hierarchy-icon" size={16} color={config.color} />
        )}
        <span className="asset-detail__hierarchy-name">{node.name}</span>
        <span
          className="asset-detail__hierarchy-badge"
          style={{ color: config.color, backgroundColor: config.bgColor }}
        >
          {config.label}
        </span>
      </div>
      {hasChildren &&
        node.children.map((child: any) => (
          <HierarchyNode
            key={child.id}
            node={child}
            depth={depth + 1}
            onSelectSource={onSelectSource}
          />
        ))}
    </>
  );
};

interface AssetDetailProps {
  node: any;
  breadcrumb: any[];
  templateName: string;
  onSelectNode: (nodeId: string) => void;
  onDuplicate: () => void;
  onMove: () => void;
  onAddNew: () => void;
  onAddTemplate: () => void;
  onAddGroup: () => void;
  onAddAsset: () => void;
  onAddDailyTask: () => void;
  onAddPrevMaint: () => void;
  onAddProject: () => void;
  onAddInventory: () => void;
  onArchiveTask: (task: any) => void;
  onOpenOccurrence?: (occurrence: TaskOccurrence) => void;
  onUnlinkInventory: (item: any) => void;
  onEditGroup?: (group: any) => void;
  onEditAsset?: (item: any) => void;
  onEditTemplate?: (template: any) => void;
  onArchiveNode?: (node: any) => void;
  onUnarchiveNode?: (node: any) => void;
  onSelectSource?: (source: { id: number; kind: 'group' | 'item' } | null | undefined) => void;
}

const AssetDetail: React.FC<AssetDetailProps> = ({
  node,
  breadcrumb,
  templateName,
  onSelectNode,
  onDuplicate,
  onMove,
  onAddNew,
  onAddTemplate,
  onAddGroup,
  onAddAsset,
  onAddDailyTask,
  onAddPrevMaint,
  onAddProject,
  onAddInventory,
  onArchiveTask,
  onOpenOccurrence,
  onUnlinkInventory,
  onEditGroup,
  onEditAsset,
  onEditTemplate,
  onArchiveNode,
  onUnarchiveNode,
  onSelectSource,
}) => {
  const intl = useIntl();
  const isTemplate = node?.type === 'template';
  const isGroup = node?.type === 'group';
  const aggregatesFromChildren = isTemplate || isGroup;
  const dailyTasks = aggregatesFromChildren
    ? node?.allDailyTasks || node?.dailyTasks || []
    : node?.dailyTasks || [];
  const preventiveMaintenance = aggregatesFromChildren
    ? node?.allPreventiveMaintenance || node?.preventiveMaintenance || []
    : node?.preventiveMaintenance || [];
  const projects = aggregatesFromChildren
    ? node?.allProjects || node?.projects || []
    : node?.projects || [];
  const inventory = node?.inventory || [];
  const children = node?.children || [];
  const showTaskSections = isTemplate || isGroup || node?.type === 'item';
  const showSource = aggregatesFromChildren;
  const currentNodeName: string | undefined = node?.name;
  const showTabs = isTemplate || isGroup;
  const [activeTab, setActiveTab] = useState<'overview' | 'contents'>('overview');
  useEffect(() => {
    setActiveTab('overview');
  }, [node?.id]);

  const overviewPanel = showTaskSections ? (
    <div className="asset-detail__sections">
      <Row>
        <Col lg={6}>
          <DailyTasksSection
            tasks={dailyTasks}
            onAdd={onAddDailyTask}
            onOpenOccurrence={onOpenOccurrence}
            onArchive={onArchiveTask}
            showSource={showSource}
            hideSourceName={currentNodeName}
            onSelectSource={onSelectSource}
          />
          <ProjectsSection
            projects={projects}
            onAdd={onAddProject}
            onOpenOccurrence={onOpenOccurrence}
            onArchive={onArchiveTask}
            showSource={showSource}
            hideSourceName={currentNodeName}
            onSelectSource={onSelectSource}
          />
        </Col>
        <Col lg={6}>
          <PreventiveMaintenanceSection
            items={preventiveMaintenance}
            onAdd={onAddPrevMaint}
            onOpenOccurrence={onOpenOccurrence}
            onArchive={onArchiveTask}
            showSource={showSource}
            hideSourceName={currentNodeName}
            onSelectSource={onSelectSource}
          />
          <InventorySection
            items={inventory}
            onAdd={onAddInventory}
            onUnlink={onUnlinkInventory}
            showSource={showSource}
            hideSourceName={currentNodeName}
            onSelectSource={onSelectSource}
          />
        </Col>
      </Row>
    </div>
  ) : null;

  const contentsPanel = (
    <div className="asset-detail__hierarchy asset-detail__hierarchy--tab-panel">
      {children.length > 0 ? (
        <div className="asset-detail__hierarchy-list">
          {children.map((child: any) => (
            <HierarchyNode key={child.id} node={child} depth={0} onSelectSource={onSelectSource} />
          ))}
        </div>
      ) : (
        <div className="asset-detail__hierarchy-empty">
          {intl.formatMessage({ id: 'assets.detail.contentsEmpty' })}
        </div>
      )}
    </div>
  );

  return (
    <div className="asset-detail">
      <AssetDetailHeader
        node={node}
        breadcrumb={breadcrumb}
        templateName={templateName}
        onSelectNode={onSelectNode}
        onDuplicate={onDuplicate}
        onMove={onMove}
        onAddNew={onAddNew}
        onAddTemplate={onAddTemplate}
        onAddGroup={onAddGroup}
        onAddAsset={onAddAsset}
        onEditGroup={onEditGroup}
        onEditAsset={onEditAsset}
        onEditTemplate={onEditTemplate}
        onArchiveNode={onArchiveNode}
        onUnarchiveNode={onUnarchiveNode}
      />

      {showTabs && (
        <div className="asset-detail__tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'overview'}
            className={`asset-detail__tab-btn${
              activeTab === 'overview' ? ' asset-detail__tab-btn--active' : ''
            }`}
            onClick={() => setActiveTab('overview')}
          >
            {intl.formatMessage({ id: 'assets.detail.overview' })}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'contents'}
            className={`asset-detail__tab-btn${
              activeTab === 'contents' ? ' asset-detail__tab-btn--active' : ''
            }`}
            onClick={() => setActiveTab('contents')}
          >
            {intl.formatMessage({ id: 'assets.detail.contents' })}
            {children.length > 0 && (
              <span className="asset-detail__tab-count">{children.length}</span>
            )}
          </button>
        </div>
      )}

      {showTabs ? (activeTab === 'contents' ? contentsPanel : overviewPanel) : overviewPanel}
    </div>
  );
};

export default AssetDetail;
